import { useEffect, useRef, useState } from "react"
import { DocumentCardFragment, ProjectFragment } from "../../../types.graphql"
import { useAnimationControls, useInView } from "framer-motion"
import { format } from "date-fns"
import { Chip } from "@components/atoms/Chip"
import { Link } from "eddev/routing"
import { useIsMobile } from "@hooks/useViewportDerived"
import { twMerge } from "tailwind-merge"

export const AnimateRow = ({ article, order }: { article: DocumentCardFragment | ProjectFragment; order: number }) => {
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { once: true, amount: 0.2 })
  const controls = useAnimationControls()
  const isMobile = useIsMobile()

  useEffect(() => {
    if (inView) {
      controls.set({
        y: -20,
        opacity: 0,
      })
      controls.start(() => ({
        y: 0,
        opacity: 1,
        transition: {
          delay: order * 0.05,
          ease: "easeOut",
        },
      }))
    } else {
      controls.set({
        opacity: 1,
      })
    }
  }, [inView])

  const [hoverState, setHoverState] = useState<boolean>(false)
  const [clickState, setClickState] = useState<boolean>(false)

  return (
    <Link
      className={twMerge(
        "grid grid-cols-12 gap-3 md:gap-4 w-screen sm:w-full -ml-4 sm:ml-0 py-4 md:py-3 px-4 md:px-2 box-border hover:bg-mid-grey hover:text-white focus:bg-yellow focus:text-black aaa-animate",
        order % 2 ? "bg-light-grey" : "",
      )}
      href={article.uri}
      onMouseOver={() => setHoverState(true)}
      onMouseOut={() => setHoverState(false)}
      onMouseDown={() => setClickState(true)}
      onMouseUp={() => setClickState(false)}
    >
      <span className="type-label-caps col-span-4 md:col-span-2 flex items-center justify-end md:justify-start order-3 md:order-1">
        {article?.date && format(article.date, "dd.MM.yyyy")}
      </span>
      <div className="!type-body-s !font-semibold col-span-12 md:col-span-6 flex items-center order-1 md:order-2">
        <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis">{article.title}</span>
      </div>

      {article.__typename === "Document" && (
        <>
          <div className="col-span-8 md:col-span-3 flex items-center order-2 type-body-s">
            <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis">
              {article.documentMeta?.subtitle}
            </span>
          </div>
          {!isMobile && (
            <div className="order-4 col-span-1 flex items-center justify-end">
              {article.documentTypes?.nodes.map((type, k) => (
                <Chip color={clickState ? "outline" : hoverState ? "outlineReversed" : "outline"} {...type} key={k}>
                  {type.name}
                </Chip>
              ))}
            </div>
          )}
        </>
      )}

      {article.__typename === "RsrpProject" && (
        <>
          <div className="col-span-8 md:col-span-4 flex items-center order-2 type-body-s">
            {article.projectMeta?.projectDetails?.inProgress ? (
              <>
                {article.projectMeta.projectDetails.estimatedCompletionDate !== "" ? (
                  <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis">
                    {article.projectMeta.projectDetails.estimatedCompletionDate}
                  </span>
                ) : (
                  <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis">In progress</span>
                )}
              </>
            ) : (
              <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis">
                {article.projectMeta?.projectDetails?.completionDate}
              </span>
            )}
          </div>
          {!isMobile && (
            <div className="order-4 col-span-1 flex items-center justify-end">
              {article.rsrpFocusAreas?.nodes.map((type, k) => (
                <Chip color={clickState ? "outline" : hoverState ? "outlineReversed" : "outline"} {...type} key={k}>
                  {type.name}
                </Chip>
              ))}
            </div>
          )}
        </>
      )}
    </Link>
  )
}
